import { Head, Link, router, useForm } from "@inertiajs/react"
import { Trash2 } from "lucide-react"

import AppLayout from "@/layouts/app-layout"
import {
  companyResourcesPath,
  companySignaturePath,
  companySignaturesPath,
  editCompanySignaturePath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

interface SignatureData {
  id: number
  name: string
  body: string | null
  status: number
}

interface StatusOption {
  value: number
  label: string
}

interface Props {
  signature: SignatureData
  status_options: StatusOption[]
  errors?: Record<string, string[]>
}

const MERGE_FIELDS = [
  "{{empName}}",
  "{{empTitle}}",
  "{{empEmail}}",
  "{{empCell}}",
  "{{empProfile}}",
  "{{empVCard}}",
]

export default function Edit({ signature, status_options, errors }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Company", href: companyResourcesPath() },
    { title: "Signatures", href: companySignaturesPath() },
    { title: "Edit", href: editCompanySignaturePath(signature.id) },
  ]

  const { data, setData, patch, processing, transform } = useForm({
    name: signature.name ?? "",
    body: signature.body ?? "",
    status: signature.status,
  })

  transform((data) => ({ signature: data }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(companySignaturePath(signature.id))
  }

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this signature?")) {
      router.delete(companySignaturePath(signature.id))
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Edit Signature" />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Update Signature</h1>
          <button
            type="button"
            onClick={handleDelete}
            className="text-hpi-red flex items-center gap-1.5 text-sm hover:text-red-700"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>

        <form onSubmit={handleSubmit} className="max-w-xl">
          <div className="mb-4">
            <label htmlFor="name" className="mb-1 block font-medium">
              Name
            </label>
            <input
              type="text"
              id="name"
              value={data.name}
              onChange={(e) => setData("name", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              maxLength={50}
            />
            {errors?.name && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.name.join(", ")}
              </p>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="body" className="mb-1 block font-medium">
              Text
            </label>
            <textarea
              id="body"
              value={data.body}
              onChange={(e) => setData("body", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              rows={10}
            />
            {errors?.body && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.body.join(", ")}
              </p>
            )}
          </div>

          <div className="bg-hpi-pale-blue mb-4 rounded p-3 text-sm">
            <span className="font-medium">Merge Fields:</span>{" "}
            {MERGE_FIELDS.join("  ")}
          </div>

          <div className="mb-4">
            <label htmlFor="status" className="mb-1 block font-medium">
              Status
            </label>
            <select
              id="status"
              value={data.status}
              onChange={(e) => setData("status", Number(e.target.value))}
              className="border-hpi-light-gray rounded border px-3 py-2"
            >
              {status_options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={processing}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
            >
              Save
            </button>
            <Link
              href={companySignaturesPath()}
              className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
            >
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
